import { useState, useRef } from 'react';
import { useAuthStore } from '@/context/authStore';
import { useTenantProfile, useUpdateTenantProfile, useUploadTenantLogo, useDeleteTenantLogo } from '@/hooks/useTenant';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Camera,
  Trash2,
  Save,
  Settings,
  Loader2,
  X,
} from 'lucide-react';

export default function VetProfile() {
  const { user } = useAuthStore();
  const { data: tenant, isLoading } = useTenantProfile();
  const updateProfile = useUpdateTenantProfile();
  const uploadLogo = useUploadTenantLogo();
  const deleteLogo = useDeleteTenantLogo();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    calendar_start_hour: 8,
    calendar_end_hour: 22,
    price_by_payment_method: false,
    cash_rounding_enabled: false,
    card_single_installment_as_cash: false,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = user?.role === 'admin';

  // Inicializar form cuando llega el tenant
  const initForm = () => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        address: tenant.address || '',
        phone: tenant.phone || '',
        calendar_start_hour: tenant.calendar_start_hour ?? 8,
        calendar_end_hour: tenant.calendar_end_hour ?? 22,
        price_by_payment_method: tenant.price_by_payment_method ?? false,
        cash_rounding_enabled: tenant.cash_rounding_enabled ?? false,
        card_single_installment_as_cash: tenant.card_single_installment_as_cash ?? false,
      });
    }
  };

  const handleEdit = () => {
    initForm();
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    await updateProfile.mutateAsync(formData);
    setIsEditing(false);
  };

  const handleLogoClick = () => {
    if (isAdmin && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadLogo.mutateAsync(file);
      } finally {
        // Resetear el input para permitir subir el mismo archivo nuevamente
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (confirm('¿Eliminar el logo de la veterinaria?')) {
      await deleteLogo.mutateAsync();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-8 text-gray-500">
        No se pudo cargar la informacion de la veterinaria
      </div>
    );
  }

  const logoUrl = tenant.logo_url
    ? `${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}${tenant.logo_url}`
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Perfil de la Veterinaria</h1>
        <p className="text-gray-600">Informacion y configuracion de tu veterinaria</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Card del Logo */}
        <div className="card">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Logo</h2>
            <p className="text-sm text-gray-500">Imagen de tu veterinaria</p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <div
              className={`relative ${isAdmin ? 'cursor-pointer group' : ''}`}
              onClick={handleLogoClick}
            >
              {logoUrl ? (
                <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-gray-100">
                  <img
                    src={logoUrl}
                    alt={tenant.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-32 w-32 rounded-full bg-primary-100 flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-primary-600" />
                </div>
              )}
              {isAdmin && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="h-8 w-8 text-white" />
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />

            {isAdmin && tenant.logo_url && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDeleteLogo}
                loading={deleteLogo.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar logo
              </Button>
            )}

            {uploadLogo.isPending && (
              <p className="text-sm text-gray-500">Subiendo...</p>
            )}
          </div>
        </div>

        {/* Card de Informacion */}
        <div className="card md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Informacion General</h2>
              <p className="text-sm text-gray-500">Datos de contacto de la veterinaria</p>
            </div>
            {isAdmin && !isEditing && (
              <Button variant="secondary" onClick={handleEdit}>
                <Settings className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Nombre de la Veterinaria"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Veterinaria San Roque"
              />

              <Input
                label="Direccion"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Ej: Av. Corrientes 1234, CABA"
              />

              <Input
                label="Telefono"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Ej: 11-1234-5678"
              />

              {/* Horario del calendario de turnos */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 mb-1">Horario del calendario de turnos</p>
                <p className="text-xs text-gray-500 mb-3">
                  Franja horaria que se muestra en Peluquería y Turnos Médicos.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Desde</label>
                    <select
                      value={formData.calendar_start_hour}
                      onChange={(e) => setFormData({ ...formData, calendar_start_hour: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Hasta</label>
                    <select
                      value={formData.calendar_end_hour}
                      onChange={(e) => setFormData({ ...formData, calendar_end_hour: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => (
                        <option key={h} value={h}>{`${String(h).padStart(2, '0')}:00`}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {formData.calendar_end_hour <= formData.calendar_start_hour && (
                  <p className="text-xs text-red-500 mt-1">La hora de fin debe ser mayor que la de inicio.</p>
                )}
              </div>

              {/* Precios según medio de pago */}
              <div className="pt-4 border-t">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.price_by_payment_method}
                    onChange={(e) => setFormData({ ...formData, price_by_payment_method: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Precios según medio de pago</span>
                    <span className="block text-xs text-gray-500">
                      Cobra distinto según el pago: <strong>efectivo</strong> (precio efectivo),
                      <strong> electrónicos</strong> —débito, transferencia y crédito 1 cuota— (precio de tarjeta) y
                      <strong> crédito en cuotas</strong> (precio de cuotas, se carga por producto).
                      Si está apagado, funciona como hasta ahora.
                    </span>
                  </span>
                </label>
              </div>

              {/* Redondeo en efectivo */}
              <div className="pt-4 border-t">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.cash_rounding_enabled}
                    onChange={(e) => setFormData({ ...formData, cash_rounding_enabled: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Redondeo en ventas de efectivo</span>
                    <span className="block text-xs text-gray-500">
                      Muestra un botón <strong>"Aplicar redondeo"</strong> al cobrar en efectivo, que
                      redondea el total al <strong>100 más cercano</strong> (ej. $430 → $400, $451 → $500).
                      El cajero decide en cada venta. Si está apagado, no aparece.
                    </span>
                  </span>
                </label>
              </div>

              {/* Tarjeta en 1 cuota al precio de efectivo */}
              <div className="pt-4 border-t">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.card_single_installment_as_cash}
                    onChange={(e) =>
                      setFormData({ ...formData, card_single_installment_as_cash: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">
                      Tarjeta en 1 cuota al precio de efectivo
                    </span>
                    <span className="block text-xs text-gray-500">
                      Cobra <strong>débito y crédito en 1 cuota</strong> al mismo precio que en
                      efectivo, sin recargo. El crédito en cuotas sigue con su precio. Sirve si no
                      trasladás el costo de la tarjeta cuando no hay financiación: así no hay que
                      hacer un descuento a mano, que además no da igual (un recargo del 10% no se
                      cancela con un descuento del 10%).
                    </span>
                  </span>
                </label>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleSave}
                  loading={updateProfile.isPending}
                  disabled={formData.calendar_end_hour <= formData.calendar_start_hour}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.name}</p>
                  <p className="text-sm text-gray-500">Nombre</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.address || 'No especificada'}</p>
                  <p className="text-sm text-gray-500">Direccion</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.phone || 'No especificado'}</p>
                  <p className="text-sm text-gray-500">Telefono</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.email || 'No especificado'}</p>
                  <p className="text-sm text-gray-500">Email</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info adicional */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Informacion del Sistema</h2>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-sm text-gray-500">Identificador</p>
            <p className="font-mono text-sm text-gray-900">{tenant.slug}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Creada el</p>
            <p className="text-sm text-gray-900">
              {new Date(tenant.created_at).toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Estado</p>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              tenant.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {tenant.is_active ? 'Activa' : 'Inactiva'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
